import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Stack, Checkbox, FormControlLabel, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, Marker, Circle } from '@react-google-maps/api';
import { TripData, UserActivity, AppOpenData } from './types';
import { City } from '../../../../resources/types/citiesTypes';

interface TripMapViewProps {
  tripsData: TripData[];
  userActivityData: UserActivity[];
  appOpenData: AppOpenData | null;
  selectedCity: City | undefined;
}

// Haversine formula to calculate distance between two coordinates in km
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const TripMapView: React.FC<TripMapViewProps> = ({ tripsData, userActivityData, appOpenData, selectedCity }) => {
  const theme = useTheme();
  
  const [showStartLocations, setShowStartLocations] = useState(true);
  const [showEndLocations, setShowEndLocations] = useState(true);
  const [showAvailableLocations, setShowAvailableLocations] = useState(true);
  const [showAppOpens, setShowAppOpens] = useState(true);

  // Calculate unique scooter deployments by workers
  const uniqueDeployments = useMemo(() => {
    const deploymentsMap = new Map<string, { scooterId: string; time: Date; location: [number, number] }>();
    
    tripsData.forEach(trip => {
      if (trip.availableSetTime && trip.availableSetLocation) {
        const key = `${trip.scooterId}_${trip.availableSetTime.getTime()}`;
        if (!deploymentsMap.has(key)) {
          deploymentsMap.set(key, {
            scooterId: trip.scooterId,
            time: trip.availableSetTime,
            location: trip.availableSetLocation
          });
        }
      }
    });
    
    return Array.from(deploymentsMap.values());
  }, [tripsData]);

  // Filter app opens by 15km radius from city center
  const filteredAppOpenCoords = useMemo(() => {
    if (!appOpenData?.coords || !selectedCity?.lat || !selectedCity?.lon) {
      return appOpenData?.coords || [];
    }

    const MAX_DISTANCE_KM = 15;
    return appOpenData.coords.filter(coord => {
      const distance = calculateDistance(selectedCity.lat!, selectedCity.lon!, coord.lat, coord.lon);
      return distance <= MAX_DISTANCE_KM;
    });
  }, [appOpenData, selectedCity]);

  const mapCenter = useMemo((): google.maps.LatLngLiteral => {
    if (tripsData.length > 0) {
      const lat = tripsData.reduce((sum, trip) => sum + trip.startLocation[0], 0) / tripsData.length;
      const lng = tripsData.reduce((sum, trip) => sum + trip.startLocation[1], 0) / tripsData.length;
      return { lat, lng };
    }
    if (userActivityData.length > 0) {
      const lat = userActivityData.reduce((sum, activity) => sum + activity.location[0], 0) / userActivityData.length;
      const lng = userActivityData.reduce((sum, activity) => sum + activity.location[1], 0) / userActivityData.length;
      return { lat, lng };
    }
    return { lat: 48.9226, lng: 24.7111 };
  }, [tripsData, userActivityData]);

  const mapOptions = useMemo(
    (): google.maps.MapOptions => ({
      styles: theme.palette.mode === 'dark'
        ? [
            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
            {
              featureType: 'administrative.locality',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'poi',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'geometry',
              stylers: [{ color: '#263c3f' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#6b9a76' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#38414e' }]
            },
            {
              featureType: 'road',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#212a37' }]
            },
            {
              featureType: 'road',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#9ca5b3' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry',
              stylers: [{ color: '#746855' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#1f2835' }]
            },
            {
              featureType: 'road.highway',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#f3d19c' }]
            },
            {
              featureType: 'transit',
              elementType: 'geometry',
              stylers: [{ color: '#2f3948' }]
            },
            {
              featureType: 'transit.station',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#d59563' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#17263c' }]
            },
            {
              featureType: 'water',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#515c6d' }]
            },
            {
              featureType: 'water',
              elementType: 'labels.text.stroke',
              stylers: [{ color: '#17263c' }]
            }
          ]
        : []
    }),
    [theme.palette.mode]
  );

  if (tripsData.length === 0 && userActivityData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E' }}>
          Завантажте дані для перегляду карти
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 2
          }}
        >
          Шари карти
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={showStartLocations}
                onChange={(e) => setShowStartLocations(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { color: '#4CAF50' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14 }}>
                🟢 Початок поїздок ({tripsData.length})
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showEndLocations}
                onChange={(e) => setShowEndLocations(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { color: '#F44336' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14 }}>
                🔴 Завершення поїздок ({tripsData.length})
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showAvailableLocations}
                onChange={(e) => setShowAvailableLocations(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { color: '#2196F3' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14 }}>
                🔵 Виставлення самокатів ({uniqueDeployments.length})
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showAppOpens}
                onChange={(e) => setShowAppOpens(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { color: '#9C27B0' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14 }}>
                🟣 Відкриття додатку без оренди ({filteredAppOpenCoords.length})
              </Typography>
            }
          />
        </Stack>
      </Paper>

      <Paper
        sx={{
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2,
          overflow: 'hidden',
          height: 600
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={13}
          options={mapOptions}
        >
          {showStartLocations && tripsData.map((trip, index) => (
            <Circle
              key={`start-${index}`}
              center={{ lat: trip.startLocation[0], lng: trip.startLocation[1] }}
              radius={50}
              options={{
                fillColor: '#4CAF50',
                fillOpacity: 0.6,
                strokeColor: '#4CAF50',
                strokeOpacity: 0.8,
                strokeWeight: 1
              }}
            />
          ))}

          {showEndLocations && tripsData.map((trip, index) => (
            <Circle
              key={`end-${index}`}
              center={{ lat: trip.endLocation[0], lng: trip.endLocation[1] }}
              radius={50}
              options={{
                fillColor: '#F44336',
                fillOpacity: 0.6,
                strokeColor: '#F44336',
                strokeOpacity: 0.8,
                strokeWeight: 1
              }}
            />
          ))}

          {showAvailableLocations && uniqueDeployments.map((deployment, index) => (
            <Circle
              key={`available-${deployment.scooterId}-${deployment.time.getTime()}`}
              center={{ lat: deployment.location[0], lng: deployment.location[1] }}
              radius={50}
              options={{
                fillColor: '#2196F3',
                fillOpacity: 0.6,
                strokeColor: '#2196F3',
                strokeOpacity: 0.8,
                strokeWeight: 1
              }}
            />
          ))}

          {showAppOpens && filteredAppOpenCoords.map((coord, index) => (
            <Circle
              key={`app-open-${index}`}
              center={{ lat: coord.lat, lng: coord.lon }}
              radius={40}
              options={{
                fillColor: '#9C27B0',
                fillOpacity: 0.6,
                strokeColor: '#9C27B0',
                strokeOpacity: 0.8,
                strokeWeight: 1
              }}
            />
          ))}
        </GoogleMap>
      </Paper>
    </Box>
  );
};

export default TripMapView;
